import {Form, useRouteLoaderData, useSearchParams, useNavigation} from 'react-router'
import {useEffect, useId, useRef, useState, type ReactNode} from 'react'
import {Button} from '@ui/Button/button'
import {track} from '@core/utils/analytics'
import {SignInCodeForm} from '@core/auth-ui/SignInCodeForm/SignInCodeForm'
import {DEFAULT_SIGN_IN_COPY, type SignInCopy} from '@core/auth-ui/signInCopy'
import type {loader as rootLoader} from '@root'
import styles from './MagicLinkForm.module.scss'

/**
 * The link sent from this tab, remembered by the tab. Sending reloads the document (React Router
 * treats the action's redirect back to the same page as a reload) and the `?magic-link-sent=1` it
 * carried does not survive, so a gate that shows a waiting view keeps its own record: the address
 * and when, for as long as the link lives.
 */
const SENT_STORAGE_KEY = 'magicLinkSent'
const LINK_LIFETIME_MS = 15 * 60 * 1000

function rememberSent(email: string) {
    try {
        sessionStorage.setItem(SENT_STORAGE_KEY, JSON.stringify({email, at: Date.now()}))
    } catch {
        // Storage blocked: the waiting view then falls back to the form.
    }
}

/** The address a still-live link went to from this tab, or ''. */
function recalledSent(): string {
    try {
        const stored = JSON.parse(sessionStorage.getItem(SENT_STORAGE_KEY) ?? 'null') as {email?: string; at?: number} | null
        return stored?.email && stored.at && Date.now() - stored.at < LINK_LIFETIME_MS ? stored.email : ''
    } catch {
        return ''
    }
}

/**
 * True once the server has sent the link and sent the person back (`?magic-link-sent=1`). With
 * `remembered`, also while this tab holds a live link of its own (see `recalledSent`).
 */
export function useMagicLinkSent(remembered = false): boolean {
    const [searchParams] = useSearchParams()
    const [held, setHeld] = useState(false)
    useEffect(() => {
        if (remembered) setHeld(recalledSent() !== '')
    }, [remembered])
    return searchParams.get('magic-link-sent') === '1' || held
}

type MagicLinkFormProps = {
    autoFocus?: boolean
    redirectTo?: string
    /** See SignInOptions — same trigger/packSlug threaded one level down. */
    trigger?: string
    packSlug?: string
    copy?: SignInCopy
    /** Shown under the waiting view (`copy.waiting`): the way out of waiting for the link. */
    alternative?: ReactNode
}

export function MagicLinkForm({autoFocus, redirectTo, trigger = 'unspecified', packSlug, copy = DEFAULT_SIGN_IN_COPY, alternative}: MagicLinkFormProps) {
    // Single source of truth: the shared root loader mints the nonce once per session and every
    // mount of this form (product login pages, every SignupGate site) reads it from here rather
    // than threading it down as a prop — see root.tsx's loader comment for why a prop chain that
    // requires each mount site to remember to mint/pass it is exactly what caused the bug.
    const rootData = useRouteLoaderData<typeof rootLoader>('root')
    const nonce = rootData?.magicLinkNonce
    const [searchParams] = useSearchParams()
    const navigation = useNavigation()
    const [email, setEmail] = useState('')
    // Unique per instance: the pack auth gate mounts two MagicLinkForms (one per breakpoint, only one
    // CSS-visible), so a static id would duplicate in the DOM. useId keeps label→input valid for each.
    const emailId = useId()
    const sent = useMagicLinkSent(!!copy.waiting)
    const errorParam = searchParams.get('magic-link-error')
    const errorMessage = copy.errors(errorParam)
    const submitting = navigation.state === 'submitting'
    // Fires once per mount that lands in the "sent" state — this is only reachable via the
    // server's redirect after it actually called sendMagicLinkEmail (see magic_link_sent's
    // comment in @core/utils/analytics.ts). Guard against StrictMode's double-invoke and against
    // this component re-rendering while `sent` stays true.
    const sentFiredRef = useRef(false)

    useEffect(() => {
        if (sent || errorMessage) {
            const url = new URL(window.location.href)
            url.searchParams.delete('magic-link-sent')
            url.searchParams.delete('magic-link-error')
            window.history.replaceState({}, '', url.pathname + url.search)
        }
    }, [sent, errorMessage])

    // Sending the link reloads the document, so the field's state is gone by the time the sent view
    // mounts: the address comes back from the tab's own storage.
    useEffect(() => {
        if (sent && !email) setEmail(recalledSent())
    }, [sent, email])

    useEffect(() => {
        if (sent && !sentFiredRef.current) {
            sentFiredRef.current = true
            track('magic_link_sent', {trigger, packSlug})
        }
    }, [sent, trigger, packSlug])

    // The email is held here, in the page's state: the address is never put in the URL (page
    // locations reach analytics and access logs). A page that lost it falls back to the form.
    const waiting = copy.waiting
    if (sent && waiting && email) {
        return (
            <div className={styles.waiting} role="status">
                <h3 className={styles.waitingTitle}>{waiting.title}</h3>
                <p className={styles.waitingBody}>{waiting.body(email)}</p>
                <Form method="POST" action="/auth/magic-link" className={styles.resend}>
                    <input type="hidden" name="nonce" value={nonce ?? ''} />
                    <input type="hidden" name="email" value={email} />
                    {redirectTo && <input type="hidden" name="redirectTo" value={redirectTo} />}
                    <input type="hidden" name="trigger" value={trigger} />
                    <Button type="submit" variant="secondary" size="large" className={styles.resendButton} disabled={submitting}>
                        {waiting.sendAgain}
                    </Button>
                </Form>
                <SignInCodeForm copy={copy.code} />
                {alternative}
            </div>
        )
    }
    if (sent && !waiting) {
        return (
            <>
                <p className={styles.sentMessage}>{copy.sent}</p>
                <SignInCodeForm copy={copy.code} />
            </>
        )
    }

    return (
        <Form
            method="POST"
            action="/auth/magic-link"
            onSubmit={() => {
                if (copy.waiting) rememberSent(email)
                track('auth_option_selected', {method: 'email', trigger, packSlug})
            }}
        >
            <input type="hidden" name="nonce" value={nonce ?? ''} />
            {redirectTo && <input type="hidden" name="redirectTo" value={redirectTo} />}
            {/* Read by magic-link.server.ts's send action and stashed onto the session the same
                way redirectTo is — survives the emailed-link roundtrip so the post-verify sign_up
                event (funnel spec §2) can carry it. */}
            <input type="hidden" name="trigger" value={trigger} />
            <div className={styles.emailRow}>
                <label htmlFor={emailId} className="sr-only">{copy.emailLabel}</label>
                <input type="email" name="email" id={emailId} placeholder="your@email.com"
                    required value={email} onChange={e => setEmail(e.target.value)}
                    className={styles.emailInput} autoFocus={autoFocus} />
                <Button type="submit" variant="secondary" size="large"
                    className={styles.sendButton}
                    disabled={submitting}>
                    {submitting ? copy.sending : copy.sendLink}
                </Button>
            </div>
            {errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}
        </Form>
    )
}
